import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import iconCircle from 'stile-shared/assets/icons/icon_circle.svg';
import iconCircleTick from 'stile-shared/assets/icons/icon_circle_tick.svg';
import iconRightArrow from 'stile-shared/assets/icons/icon_right_arrow.svg';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import { Icon } from 'stile-shared/src/components/2024/common/Icon';
import { Slider } from 'stile-shared/src/components/2024/common/Slider';
import { Underline } from 'stile-shared/src/components/2024/common/Underline';
import { Grid } from 'stile-shared/src/components/2024/layout/Grid';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

import { PageLayout } from 'templates/2024/PageLayout';

export { Head } from 'templates/2024/Head';

const OregonAlignmentLayout = styled<typeof PageLayout>(PageLayout)`
  .main-content {
    margin-top: -12px;
  }
`;

const OregonAlignmentTopSection = styled.div`
  background-color: transparent;

  .content {
    display: flex;
    flex-direction: column;
    position: relative;
    padding-bottom: 100px;

    @media (max-width: 768px) {
      padding-bottom: 48px;
    }

    h1 {
      margin-top: 80px;
      margin-bottom: 60px;
      width: 60%;

      @media (max-width: 768px) {
        margin-top: 38px;
        margin-bottom: 25px;
        width: 100%;
      }
    }

    > div {
      width: 38%;

      @media (max-width: 768px) {
        width: 100%;
      }
    }

    .underline {
      margin-top: 20px;
      margin-bottom: 30px;
    }
  }
`;

const SwipeAdviceSection = styled.div`
  .content {
    display: none;
    flex-direction: row;
    gap: 10px;
    padding-bottom: 12px;
    color: #706d69;
    p {
      margin: 0;
      padding: 0;
    }

    @media (max-width: 768px) {
      display: flex;
    }
  }
`;

const AlignmentSection = styled.div`
  background-color: transparent;

  .content {
    display: flex;
    flex-direction: column;
    position: relative;
    padding-bottom: 100px;

    .table {
      border-radius: 4px;
      border: var(--stile-grey-8) 1px solid;
      display: grid;
      grid-template-columns: 200px 100px 200px 200px 1fr;
      overflow-x: scroll;
      .header {
        display: contents;
        border-radius: 4px;
        > div {
          background: var(--stile-grey-2);
          padding: 10px;
          font-weight: 600;
          border-right: var(--stile-grey-3) 1px solid;
          border-bottom: var(--stile-grey-3) 1px solid;
          &:first-child {
            border-top-left-radius: 4px;
            background: var(--stile-grey-4);
          }
          &:last-child {
            border-top-right-radius: 4px;
          }
        }
      }
      .row {
        display: contents;

        > div {
          padding: 10px;
          border-right: var(--stile-grey-3) 1px solid;
          border-bottom: var(--stile-grey-3) 1px solid;

          &:first-child {
            background-color: #f0fef1;
          }
        }

        // every even child
        &:nth-child(odd) {
          > div {
            background-color: #fbfaf9;

            &:first-child {
              background-color: #d9f7da;
            }
          }
        }

        &.first-in-section {
          > div {
            border-top: var(--stile-grey-8) 1px solid;
            border-collapse: collapse;
          }
        }

        &:last-child {
          > div {
            border-bottom: none;
          }
        }
      }
    }
  }
`;

function TwentyTwentyFourOregonPage(props: PageProps) {
  return (
    <OregonAlignmentLayout {...props}>
      <OregonAlignmentTopSection>
        <div className="content">
          <h1>Oregon Science Standards Alignment</h1>
          <div>
            With a near-perfect score of 95% on the Oregon IMET, and loads of local phenomena,
            Stile's got Oregon Science Standards covered.
          </div>
          <Underline underlineNumber={1} speed="0.5s" />
          <Button style="primary" href="/states/oregon/" label="Learn about Stile in Oregon" />
        </div>
      </OregonAlignmentTopSection>
      <SwipeAdviceSection>
        <div className="content">
          <p>Swipe across to view table</p>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.24648 5.35416H5.94857C6.1201 5.35416 6.26194 5.41023 6.37409 5.52239C6.48624 5.63454 6.54232 5.77638 6.54232 5.94791C6.54232 6.11943 6.48624 6.26127 6.37409 6.37343C6.26194 6.48558 6.1201 6.54166 5.94857 6.54166H3.37565C3.15135 6.54166 2.96332 6.46579 2.81159 6.31405C2.65985 6.16232 2.58398 5.9743 2.58398 5.74999V3.17707C2.58398 3.00555 2.64006 2.86371 2.75221 2.75155C2.86437 2.6394 3.00621 2.58332 3.17773 2.58332C3.34926 2.58332 3.4911 2.6394 3.60326 2.75155C3.71541 2.86371 3.77148 3.00555 3.77148 3.17707V4.18645C4.72148 3.40798 5.77044 2.81423 6.91836 2.4052C8.06628 1.99617 9.26037 1.79166 10.5007 1.79166C12.2687 1.79166 13.852 2.1677 15.2507 2.91978C16.6493 3.67186 17.6257 4.66145 18.1798 5.88853C18.2458 6.04686 18.2392 6.1953 18.16 6.33384C18.0809 6.47239 17.9621 6.54166 17.8038 6.54166C17.6059 6.54166 17.4244 6.48888 17.2595 6.38332C17.0946 6.27777 16.9593 6.13923 16.8538 5.9677C16.26 5.03089 15.409 4.2986 14.3007 3.77082C13.1923 3.24305 11.9257 2.97916 10.5007 2.97916C9.33954 2.97916 8.22461 3.18367 7.15586 3.5927C6.08711 4.00173 5.11732 4.58888 4.24648 5.35416ZM10.3621 18.4167C10.0454 18.4167 9.74197 18.3573 9.45169 18.2385C9.16142 18.1198 8.90412 17.9483 8.67982 17.7239L5.19648 14.2208C5.05135 14.0757 4.97548 13.8943 4.96888 13.6766C4.96228 13.4588 5.03155 13.2708 5.17669 13.1125L5.23607 13.0531C5.44718 12.842 5.69458 12.7002 5.97826 12.6276C6.26194 12.555 6.54232 12.5583 6.8194 12.6375L8.12565 13.0135V6.54166C8.12565 6.31735 8.20152 6.12933 8.35326 5.97759C8.50499 5.82586 8.69301 5.74999 8.91732 5.74999C9.14162 5.74999 9.32965 5.82586 9.48138 5.97759C9.63312 6.12933 9.70899 6.31735 9.70899 6.54166V14.0625C9.70899 14.3264 9.60343 14.5375 9.39232 14.6958C9.18121 14.8542 8.9503 14.9003 8.69961 14.8344L7.78919 14.5771L9.80794 16.5958C9.87392 16.6618 9.95638 16.7179 10.0553 16.7641C10.1543 16.8102 10.2566 16.8333 10.3621 16.8333H13.6673C14.1027 16.8333 14.4755 16.6783 14.7855 16.3682C15.0956 16.0581 15.2507 15.6854 15.2507 15.25V12.0833C15.2507 11.859 15.3265 11.671 15.4783 11.5193C15.63 11.3675 15.818 11.2917 16.0423 11.2917C16.2666 11.2917 16.4546 11.3675 16.6064 11.5193C16.7581 11.671 16.834 11.859 16.834 12.0833V15.25C16.834 16.1208 16.5239 16.8663 15.9038 17.4864C15.2836 18.1066 14.5382 18.4167 13.6673 18.4167H10.3621ZM11.2923 8.91666C11.5166 8.91666 11.7046 8.99252 11.8564 9.14426C12.0081 9.296 12.084 9.48402 12.084 9.70832V12.0833C12.084 12.3076 12.0081 12.4956 11.8564 12.6474C11.7046 12.7991 11.5166 12.875 11.2923 12.875C11.068 12.875 10.88 12.7991 10.7283 12.6474C10.5765 12.4956 10.5007 12.3076 10.5007 12.0833V9.70832C10.5007 9.48402 10.5765 9.296 10.7283 9.14426C10.88 8.99252 11.068 8.91666 11.2923 8.91666ZM13.6673 9.70832C13.8916 9.70832 14.0796 9.78419 14.2314 9.93593C14.3831 10.0877 14.459 10.2757 14.459 10.5V12.0833C14.459 12.3076 14.3831 12.4956 14.2314 12.6474C14.0796 12.7991 13.8916 12.875 13.6673 12.875C13.443 12.875 13.255 12.7991 13.1033 12.6474C12.9515 12.4956 12.8757 12.3076 12.8757 12.0833V10.5C12.8757 10.2757 12.9515 10.0877 13.1033 9.93593C13.255 9.78419 13.443 9.70832 13.6673 9.70832Z"
              fill="#928D88"
            />
          </svg>
        </div>
      </SwipeAdviceSection>
      <AlignmentSection>
        <div className="content">
          <div className="table">
            <div className="header">
              <div>Stile unit</div>
              <div>Grade</div>
              <div>Category</div>
              <div>Code</div>
              <div>Performance expectation</div>
            </div>
            <div className="row">
              <div>Cells</div>
              <div>6</div>
              <div>From Molecules to Organisms: Structures and Processes</div>
              <div>6.LS1.1</div>
              <div>
                Conduct an investigation to provide evidence that living things are made of cells;
                either one cell or many different numbers and types of cells.
              </div>
            </div>

            <div className="row">
              <div>Cells</div>
              <div>6</div>
              <div>From Molecules to Organisms: Structures and Processes</div>
              <div>6.LS1.2</div>
              <div>
                Develop and use a model to describe the function of a cell as a whole and ways parts
                of cells contribute to the function.
              </div>
            </div>

            <div className="row">
              <div>Cells, Body Systems</div>
              <div>6</div>
              <div>From Molecules to Organisms: Structures and Processes</div>
              <div>6.LS1.3</div>
              <div>
                Construct an explanation supported by evidence for how the body is composed of
                interacting systems consisting of cells, tissues, and organs working together to
                maintain homeostasis.
              </div>
            </div>

            <div className="row">
              <div>The Survival of Species, Plants</div>
              <div>6</div>
              <div>From Molecules to Organisms: Structures and Processes</div>
              <div>6.LS1.4</div>
              <div>
                Use argument based on empirical evidence and scientific reasoning to support an
                explanation for how characteristic animal behaviors and specialized plant structures
                affect the probability of successful reproduction of animals and plants
                respectively.
              </div>
            </div>

            <div className="row">
              <div>Plants</div>
              <div>6</div>
              <div>From Molecules to Organisms: Structures and Processes</div>
              <div>6.LS1.5</div>
              <div>
                Construct a scientific explanation based on evidence for how environmental and
                genetic factors influence the growth of organisms.
              </div>
            </div>

            <div className="row">
              <div>The Nervous System</div>
              <div>6</div>
              <div>From Molecules to Organisms: Structures and Processes</div>
              <div>6.LS1.8</div>
              <div>
                Gather and synthesize information that sensory receptors respond to stimuli by
                sending messages to the brain for immediate behavior or storage as memories.
              </div>
            </div>

            <div className="row">
              <div>The Survival of Species</div>
              <div>6</div>
              <div>Heredity: Inheritance and Variation of Traits</div>
              <div>6.LS3.2</div>
              <div>
                Develop and use models to describe why asexual reproduction results in offspring
                with identical genetic information and sexual reproduction results in offspring with
                genetic variation.
              </div>
            </div>

            <div className="row">
              <div>Earth Systems</div>
              <div>6</div>
              <div>Earth’s Systems</div>
              <div>6.ESS2.4</div>
              <div>
                Develop a model to describe the cycling of water through Earth’s systems driven by
                energy from the sun and the force of gravity.
              </div>
            </div>

            <div className="row">
              <div>Earth Systems</div>
              <div>6</div>
              <div>Earth’s Systems</div>
              <div>6.ESS2.5</div>
              <div>
                Collect data to provide evidence for how the motions and complex interactions of air
                masses results in changes in weather conditions.
              </div>
            </div>

            <div className="row">
              <div>Earth Systems</div>
              <div>6</div>
              <div>Earth’s Systems</div>
              <div>6.ESS2.6</div>
              <div>
                Develop and use a model to describe how unequal heating and rotation of the Earth
                cause patterns of atmospheric and oceanic circulation that determine regional
                climates.
              </div>
            </div>

            <div className="row">
              <div>Climate Change</div>
              <div>6</div>
              <div>Earth and Human Activity</div>
              <div>6.ESS3.3</div>
              <div>
                Apply scientific principles to design a method for monitoring and minimizing a human
                impact on the environment.
              </div>
            </div>

            <div className="row">
              <div>Climate Change</div>
              <div>6</div>
              <div>Earth and Human Activity</div>
              <div>6.ESS3.5</div>
              <div>
                Ask clarifying questions based on evidence about the factors that have caused
                climate change over the past century.
              </div>
            </div>

            <div className="row">
              <div>Energy</div>
              <div>6</div>
              <div></div>
              <div>6.PS3.5</div>
              <div>
                Construct, use, and present arguments to support the claim that when the kinetic
                energy of an object changes, energy is transferred to or from the object.
              </div>
            </div>

            <div className="row">
              <div>Energy</div>
              <div>6</div>
              <div>Engineering, Technology, and the Application of Science</div>
              <div>MS.ETS1.4</div>
              <div>
                Develop a model to generate data for iterative testing and modification of a
                proposed object, tool, or process such that an optimal design can be achieved.
              </div>
            </div>

            <div className="row">
              <div>Heat</div>
              <div>6</div>
              <div>Energy</div>
              <div>6.PS3.3</div>
              <div>
                Apply scientific principles to design, construct, and test a device that either
                minimizes or maximizes thermal energy transfer.
              </div>
            </div>

            <div className="row">
              <div>Heat</div>
              <div>6</div>
              <div>Energy</div>
              <div>6.PS3.4</div>
              <div>
                Plan an investigation to determine the relationships among the energy transferred,
                the type of matter, the mass, and the change in the average kinetic energy of the
                particles as measured by the temperature of the sample.
              </div>
            </div>

            <div className="row">
              <div>Heat</div>
              <div>6</div>
              <div>Energy</div>
              <div>6.PS3.5</div>
              <div>
                Construct, use, and present arguments to support the claim that when the kinetic
                energy of an object changes, energy is transferred to or from the object.
              </div>
            </div>

            <div className="row">
              <div>Food Chains and Food Webs</div>
              <div>7</div>
              <div>Ecosystems: Interactions, Energy, and Dynamics</div>
              <div>7.LS2.2</div>
              <div>
                Construct an explanation that predicts patterns of interactions among organisms
                across multiple ecosystems.
              </div>
            </div>

            <div className="row">
              <div>Food Chains and Food Webs</div>
              <div>7</div>
              <div>Ecosystems: Interactions, Energy, and Dynamics</div>
              <div>7.LS2.3</div>
              <div>
                Develop a model to describe the cycling of matter and flow of energy among living
                and non-living parts of an ecosystem.
              </div>
            </div>

            <div className="row">
              <div>The Importance of Biodiversity</div>
              <div>7</div>
              <div>Engineering, Technology, and the Application of Science</div>
              <div>MS.ETS1.1</div>
              <div>
                Define the criteria and constraints of a design problem with sufficient precision to
                ensure a successful solution, taking into account relevant scientific principles and
                potential impacts on people and the natural environment that may limit possible
                solutions.
              </div>
            </div>

            <div className="row">
              <div>The Importance of Biodiversity</div>
              <div>7</div>
              <div>Engineering, Technology, and the Application of Science</div>
              <div>MS.ETS1.2</div>
              <div>
                Evaluate competing design solutions using a systematic process to determine how well
                they meet the criteria and constraints of the problem.
              </div>
            </div>

            <div className="row">
              <div>The Importance of Biodiversity</div>
              <div>7</div>
              <div>Ecosystems: Interactions, Energy, and Dynamics</div>
              <div>7.LS2.4</div>
              <div>
                Construct an argument supported by empirical evidence that changes to physical or
                biological components of an ecosystem affect populations.
              </div>
            </div>

            <div className="row">
              <div>The Importance of Biodiversity</div>
              <div>7</div>
              <div>Ecosystems: Interactions, Energy, and Dynamics</div>
              <div>7.LS2.5</div>
              <div>
                Evaluate competing design solutions for maintaining biodiversity and ecosystem
                services.
              </div>
            </div>

            <div className="row">
              <div>Active Earth</div>
              <div>7</div>
              <div>Earth’s Systems</div>
              <div>7.ESS2.1</div>
              <div>
                Develop a model to describe the cycling of Earth’s materials and the flow of energy
                that drives this process.
              </div>
            </div>

            <div className="row">
              <div>Active Earth</div>
              <div>7</div>
              <div>Earth’s Systems</div>
              <div>7.ESS2.2</div>
              <div>
                Construct an explanation based on evidence for how geoscience processes have changed
                Earth’s surface at varying time and spatial scales.
              </div>
            </div>

            <div className="row">
              <div>Active Earth</div>
              <div>7</div>
              <div>Earth’s Systems</div>
              <div>7.ESS2.3</div>
              <div>
                Analyze and interpret data on the distribution of fossils and rocks, continental
                shapes, and seafloor structures to provide evidence of the past plate motions.
              </div>
            </div>

            <div className="row">
              <div>Active Earth</div>
              <div>7</div>
              <div>Earth and Human Activity</div>
              <div>7.ESS3.1</div>
              <div>
                Construct a scientific explanation based on evidence for how the uneven
                distributions of Earth’s mineral, energy, and groundwater resources are the result
                of past and current geoscience processes.
              </div>
            </div>

            <div className="row">
              <div>Active Earth</div>
              <div>7</div>
              <div>Earth and Human Activity</div>
              <div>7.ESS3.2</div>
              <div>
                Analyze and interpret data on natural hazards to forecast future catastrophic events
                and inform the development of technologies to mitigate their effects.
              </div>
            </div>

            <div className="row">
              <div>States of Matter</div>
              <div>7</div>
              <div>Matter and Its Interactions</div>
              <div>7.PS1.4</div>
              <div>
                Develop a model that predicts and describes changes in particle motion, temperature,
                and state of a pure substance when thermal energy is added or removed.
              </div>
            </div>

            <div className="row">
              <div>Physical and Chemical Change</div>
              <div>7</div>
              <div>Matter and Its Interactions</div>
              <div>7.PS1.2</div>
              <div>
                Analyze and interpret data on the properties of substances before and after the
                substances interact to determine if a chemical reaction has occurred.
              </div>
            </div>

            <div className="row">
              <div>Physical and Chemical Change</div>
              <div>7</div>
              <div>Matter and Its Interactions</div>
              <div>7.PS1.6</div>
              <div>
                Undertake a design project to construct, test, and modify a device that either
                releases or absorbs thermal energy by chemical processes.
              </div>
            </div>

            <div className="row">
              <div>Ecosystems</div>
              <div>7</div>
              <div>From Molecules to Organisms: Structures and Processes</div>
              <div>7.LS1.6</div>
              <div>
                Construct a scientific explanation based on evidence for the role of photosynthesis
                in the cycling of matter and flow of energy into and out of organisms.
              </div>
            </div>

            <div className="row">
              <div>Ecosystems</div>
              <div>7</div>
              <div>From Molecules to Organisms: Structures and Processes</div>
              <div>7.LS1.7</div>
              <div>
                Develop a model to describe how food is rearranged through chemical reactions
                forming new molecules that support growth and/or release energy as this matter moves
                through an organism.
              </div>
            </div>

            <div className="row">
              <div>Ecosystems</div>
              <div>7</div>
              <div>Ecosystems: Interactions, Energy, and Dynamics</div>
              <div>7.LS2.1</div>
              <div>
                Analyze and interpret data to provide evidence for the effects of resource
                availability on organisms and populations of organisms in an ecosystem.
              </div>
            </div>

            <div className="row">
              <div>Ecosystems</div>
              <div>7</div>
              <div>Matter and Its Interactions</div>
              <div>7.PS1.3</div>
              <div>
                Gather and make sense of information to describe that synthetic materials come from
                natural resources and impact society.
              </div>
            </div>

            <div className="row">
              <div>Elements and Compounds</div>
              <div>7</div>
              <div>Matter and Its Interactions</div>
              <div>7.PS1.1</div>
              <div>
                Develop models to describe the atomic composition of simple molecules and extended
                structures.
              </div>
            </div>

            <div className="row">
              <div>Elements and Compounds</div>
              <div>7</div>
              <div>Matter and Its Interactions</div>
              <div>7.PS1.5</div>
              <div>
                Develop and use a model to describe how the total number of atoms does not change in
                a chemical reaction and thus mass is conserved.
              </div>
            </div>

            <div className="row">
              <div>Our Place in Space</div>
              <div>8</div>
              <div>Earth’s Place in the Universe</div>
              <div>8.ESS1.1</div>
              <div>
                Develop and use a model of the Earth-sun-moon system to describe the cyclic patterns
                of lunar phases, eclipses of the sun and moon, and seasons.
              </div>
            </div>

            <div className="row">
              <div>Our Place in Space</div>
              <div>8</div>
              <div>Earth’s Place in the Universe</div>
              <div>8.ESS1.2</div>
              <div>
                Develop and use a model to describe the role of gravity in the motions within
                galaxies and the solar system.
              </div>
            </div>

            <div className="row">
              <div>Our Place in Space</div>
              <div>8</div>
              <div>Earth’s Place in the Universe</div>
              <div>8.ESS1.3</div>
              <div>
                Analyze and interpret data to determine scale properties of objects in the solar
                system.
              </div>
            </div>

            <div className="row">
              <div>Energy Conservation</div>
              <div>8</div>
              <div>Energy</div>
              <div>8.PS3.1</div>
              <div>
                Construct and interpret graphical displays of data to describe the relationships of
                kinetic energy to the mass of an object and to the speed of an object.
              </div>
            </div>

            <div className="row">
              <div>Energy Conservation</div>
              <div>8</div>
              <div>Energy</div>
              <div>8.PS3.2</div>
              <div>
                Develop a model to describe that when the arrangement of objects interacting at a
                distance changes, different amounts of potential energy are stored in the system.
              </div>
            </div>

            <div className="row">
              <div>Non-contact Forces and Electricity</div>
              <div>8</div>
              <div>Engineering, Technology, and the Application of Science</div>
              <div>MS.ETS1.3</div>
              <div>
                Analyze data from tests to determine similarities and differences among several
                design solutions to identify the best characteristics of each that can be combined
                into a new solution to better meet the criteria for success.
              </div>
            </div>

            <div className="row">
              <div>Non-contact Forces and Electricity</div>
              <div>8</div>
              <div>Motion and Stability: Forces and Interactions</div>
              <div>8.PS2.3</div>
              <div>
                Ask questions about data to determine the factors that affect the strength of
                electric and magnetic forces.
              </div>
            </div>

            <div className="row">
              <div>Non-contact Forces and Electricity</div>
              <div>8</div>
              <div>Motion and Stability: Forces and Interactions</div>
              <div>8.PS2.5</div>
              <div>
                Conduct an investigation and evaluate the experimental design to provide evidence
                that fields exist between objects exerting forces on each other even though the
                objects are not in contact.
              </div>
            </div>

            <div className="row">
              <div>Waves</div>
              <div>8</div>
              <div>Waves and Their Applications in Technologies for Information Transfer</div>
              <div>8.PS4.1</div>
              <div>
                Use mathematical representations to describe a simple model for waves that includes
                how the amplitude of a wave is related to the energy in a wave.
              </div>
            </div>

            <div className="row">
              <div>Waves</div>
              <div>8</div>
              <div>Waves and Their Applications in Technologies for Information Transfer</div>
              <div>8.PS4-2</div>
              <div>
                Develop and use a model to describe that waves are reflected, absorbed, or
                transmitted through various materials.
              </div>
            </div>

            <div className="row">
              <div>Waves</div>
              <div>8</div>
              <div>Waves and Their Applications in Technologies for Information Transfer</div>
              <div>8.PS4.3</div>
              <div>
                Integrate qualitative scientific and technical information to support the claim that
                digitized signals are a more reliable way to encode and transmit information than
                analog signals.
              </div>
            </div>

            <div className="row">
              <div>Newton's Laws of Motion</div>
              <div>8</div>
              <div>Motion and Stability: Forces and Interactions</div>
              <div>8.PS2.1</div>
              <div>
                Apply Newton’s Third Law to design a solution to a problem involving the motion of
                two colliding objects.
              </div>
            </div>

            <div className="row">
              <div>Newton's Laws of Motion</div>
              <div>8</div>
              <div>Motion and Stability: Forces and Interactions</div>
              <div>8.PS2.2</div>
              <div>
                Plan an investigation to provide evidence that the change in an object’s motion
                depends on the sum of the forces on the object and the mass of the object.
              </div>
            </div>

            <div className="row">
              <div>Newton's Laws of Motion</div>
              <div>8</div>
              <div>Motion and Stability: Forces and Interactions</div>
              <div>8.PS2.4</div>
              <div>
                Construct and present arguments using evidence to support the claim that
                gravitational interactions are attractive and depend on the masses of interacting
                objects.
              </div>
            </div>

            <div className="row">
              <div>Human Impact on Ecosystems</div>
              <div>8</div>
              <div>Earth’s Place in the Universe</div>
              <div>8.ESS1.4</div>
              <div>
                Construct a scientific explanation based on evidence from rock strata for how the
                geologic time scale is used to organize Earth’s 4.6-billion-year-old history.
              </div>
            </div>

            <div className="row">
              <div>Human Impact on Ecosystems</div>
              <div>8</div>
              <div>Earth and Human Activity</div>
              <div>8.ESS3.4</div>
              <div>
                Construct an argument supported by evidence for how increases in human population
                and per-capita consumption of natural resources impact Earth’s systems.
              </div>
            </div>

            <div className="row">
              <div>Human Impact on Ecosystems</div>
              <div>8</div>
              <div>Biological Evolution: Unity and Diversity</div>
              <div>8.LS4.1</div>
              <div>
                Analyze and interpret data for patterns in the fossil record that document the
                existence, diversity, extinction, and change of life forms throughout the history of
                life on Earth under the assumption that natural laws operate today as in the past.
              </div>
            </div>

            <div className="row">
              <div>Genetics</div>
              <div>8</div>
              <div>Heredity: Inheritance and Variation of Traits</div>
              <div>8.LS3.1</div>
              <div>
                Develop and use a model to describe why structural changes to genes (mutations)
                located on chromosomes may affect proteins and may result in harmful, beneficial, or
                neutral effects to the structure and function of the organism.
              </div>
            </div>

            <div className="row">
              <div>Evolution</div>
              <div>8</div>
              <div>Biological Evolution: Unity and Diversity</div>
              <div>8.LS4.2</div>
              <div>
                Apply scientific ideas to construct an explanation for the anatomical similarities
                and differences among modern organisms and between modern and fossil organisms to
                infer evolutionary relationships.
              </div>
            </div>

            <div className="row">
              <div>Evolution</div>
              <div>8</div>
              <div>Biological Evolution: Unity and Diversity</div>
              <div>8.LS4.3</div>
              <div>
                Analyze displays of pictorial data to compare patterns of similarities in the
                embryological development across multiple species to identify relationships not
                evident in the fully formed anatomy.
              </div>
            </div>

            <div className="row">
              <div>Evolution</div>
              <div>8</div>
              <div>Biological Evolution: Unity and Diversity</div>
              <div>8.LS4.4</div>
              <div>
                Construct an explanation based on evidence that describes how genetic variations of
                traits in a population increase some individuals’ probability of surviving and
                reproducing in a specific environment.
              </div>
            </div>

            <div className="row">
              <div>Evolution</div>
              <div>8</div>
              <div>Biological Evolution: Unity and Diversity</div>
              <div>8.LS4.5</div>
              <div>
                Gather and synthesize information about the technologies that have changed the way
                humans influence the inheritance of desired traits in organisms.
              </div>
            </div>

            <div className="row">
              <div>Evolution</div>
              <div>8</div>
              <div>Biological Evolution: Unity and Diversity</div>
              <div>8.LS4.6</div>
              <div>
                Use mathematical representations to support explanations of how natural selection
                may lead to increases and decreases of specific traits in populations over time.
              </div>
            </div>
          </div>
        </div>
      </AlignmentSection>
    </OregonAlignmentLayout>
  );
}

export default TwentyTwentyFourOregonPage;
